/**
 * Batch Upload Component
 *
 * Handles multi-file upload with drag-and-drop support,
 * progress tracking, and queue management.
 */

import { t } from '../../i18n/translations.js';

/**
 * Get auth token for API requests
 */
function getAuthToken() {
  // Get token from auth service storage (stored as JSON with accessToken property)
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const { accessToken, expiresAt } = JSON.parse(authData);
      // Check if token is not expired
      if (expiresAt && new Date(expiresAt) > new Date()) {
        return accessToken;
      }
    }
  } catch (e) {
    console.warn('Failed to get auth token:', e);
  }
  return null;
}

/**
 * File upload states
 */
const UploadState = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  UPLOAD_COMPLETE: 'upload_complete',
  STORING: 'storing',
  EXTRACTING: 'extracting',
  ANALYZING: 'analyzing',
  COMPLETE: 'complete',
  ERROR: 'error',
};

/**
 * Status messages for each state with detailed descriptions
 */
const StatusMessages = {
  [UploadState.PENDING]: 'Ready to upload',
  [UploadState.UPLOADING]: 'Uploading file...',
  [UploadState.UPLOAD_COMPLETE]: 'Upload complete, processing...',
  [UploadState.STORING]: 'Saving to cloud storage...',
  [UploadState.EXTRACTING]: 'AI is reading document...',
  [UploadState.ANALYZING]: 'Analyzing extracted data...',
  [UploadState.COMPLETE]: 'Complete',
  [UploadState.ERROR]: 'Failed',
};

/**
 * Progress step descriptions for user feedback
 */
const ProgressSteps = [
  { state: UploadState.UPLOADING, label: 'Uploading', icon: 'upload' },
  { state: UploadState.STORING, label: 'Storing', icon: 'cloud' },
  { state: UploadState.EXTRACTING, label: 'Extracting', icon: 'ai' },
  { state: UploadState.ANALYZING, label: 'Analyzing', icon: 'chart' },
  { state: UploadState.COMPLETE, label: 'Done', icon: 'check' },
];

/**
 * Supported file types
 */
const SUPPORTED_TYPES = {
  'application/pdf': { ext: 'pdf', icon: 'pdf' },
  'image/png': { ext: 'png', icon: 'image' },
  'image/jpeg': { ext: 'jpg', icon: 'image' },
  'text/csv': { ext: 'csv', icon: 'spreadsheet' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: 'xlsx', icon: 'spreadsheet' },
  'application/vnd.ms-excel': { ext: 'xls', icon: 'spreadsheet' },
};

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_FILES = 10;

/**
 * Document types for upload
 */
const DocumentTypes = {
  INVOICE: 'invoice',
  CONTRACT: 'contract',
};

/**
 * Component state
 */
const state = {
  files: [], // { id, file, state, progress, error, result }
  isDragging: false,
  isUploading: false,
  documentType: DocumentTypes.INVOICE, // Default to invoice
};

/**
 * Callbacks provided during initialization
 */
let callbacks = {
  onSuccess: null,
  onError: null,
};

/**
 * Generate unique file ID
 */
function generateId() {
  return `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format file size
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Validate a single file
 */
function validateFile(file) {
  const errors = [];

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File exceeds maximum size of ${formatFileSize(MAX_FILE_SIZE)}`);
  }

  // Check file type
  if (!SUPPORTED_TYPES[file.type]) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    const validByExt = ['pdf', 'png', 'jpg', 'jpeg', 'csv', 'xlsx', 'xls'].includes(ext);
    if (!validByExt) {
      errors.push('Unsupported file type. Supported: PDF, PNG, JPG, CSV, XLSX');
    }
  }

  return errors;
}

/**
 * Get file icon SVG
 */
function getFileIcon(mimeType) {
  const typeInfo = SUPPORTED_TYPES[mimeType];
  const iconType = typeInfo?.icon || 'document';

  const icons = {
    pdf: `<svg viewBox="0 0 24 24" width="24" height="24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="2"/>
      <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="2"/>
      <path d="M9 15h6M9 11h6" stroke="currentColor" stroke-width="2"/>
    </svg>`,
    image: `<svg viewBox="0 0 24 24" width="24" height="24">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" fill="none" stroke="currentColor" stroke-width="2"/>
      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
      <polyline points="21 15 16 10 5 21" fill="none" stroke="currentColor" stroke-width="2"/>
    </svg>`,
    spreadsheet: `<svg viewBox="0 0 24 24" width="24" height="24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="2"/>
      <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="2"/>
      <line x1="8" y1="13" x2="16" y2="13" stroke="currentColor" stroke-width="2"/>
      <line x1="8" y1="17" x2="16" y2="17" stroke="currentColor" stroke-width="2"/>
      <line x1="12" y1="13" x2="12" y2="17" stroke="currentColor" stroke-width="2"/>
    </svg>`,
    document: `<svg viewBox="0 0 24 24" width="24" height="24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="2"/>
      <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="2"/>
    </svg>`,
  };

  return icons[iconType] || icons.document;
}

/**
 * Get extracted data preview for display
 */
function getExtractedPreview(extractedData) {
  if (!extractedData) return null;
  const preview = [];
  if (extractedData.vendor_name) preview.push(`Vendor: ${extractedData.vendor_name}`);
  if (extractedData.invoice_number) preview.push(`Invoice #: ${extractedData.invoice_number}`);
  if (extractedData.total_amount) preview.push(`Amount: $${extractedData.total_amount}`);
  if (extractedData.contract_title) preview.push(`Title: ${extractedData.contract_title}`);
  return preview.length > 0 ? preview : null;
}

/**
 * Simulate extraction progress with visual feedback
 */
function simulateExtractionProgress(pendingFiles, duration) {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(95, Math.round((elapsed / duration) * 100));
      
      pendingFiles.forEach(f => {
        if (f.state === UploadState.EXTRACTING || f.state === UploadState.ANALYZING) {
          f.progress = progress;
        }
      });
      render();
      
      if (elapsed >= duration) {
        clearInterval(interval);
        pendingFiles.forEach(f => {
          if (f.state === UploadState.EXTRACTING || f.state === UploadState.ANALYZING) {
            f.progress = 100;
          }
        });
        render();
        resolve();
      }
    }, 50);
  });
}

/**
 * Add files to the queue
 */
function addFiles(fileList) {
  const files = Array.from(fileList);
  
  // Check max files limit
  if (state.files.length + files.length > MAX_FILES) {
    alert(`Maximum ${MAX_FILES} files allowed at once.`);
    return;
  }

  files.forEach(file => {
    const errors = validateFile(file);
    
    state.files.push({
      id: generateId(),
      file,
      state: errors.length > 0 ? UploadState.ERROR : UploadState.PENDING,
      progress: 0,
      error: errors.join('; ') || null,
      result: null,
    });
  });

  render();
}

/**
 * Remove a file from the queue
 */
function removeFile(fileId) {
  state.files = state.files.filter(f => f.id !== fileId);
  render();
}

/**
 * Clear all files
 */
function clearFiles() {
  state.files = [];
  render();
}

/**
 * Set document type
 */
function setDocumentType(type) {
  if (type === DocumentTypes.INVOICE || type === DocumentTypes.CONTRACT) {
    state.documentType = type;
    render();
  }
}

/**
 * Upload all pending files with progress tracking
 */
async function uploadFiles() {
  const pendingFiles = state.files.filter(f => f.state === UploadState.PENDING);

  if (pendingFiles.length === 0) {
    alert('No files to upload');
    return;
  }

  state.isUploading = true;

  // Set all pending files to uploading
  pendingFiles.forEach(f => {
    f.state = UploadState.UPLOADING;
    f.progress = 0;
    f.statusMessage = StatusMessages[UploadState.UPLOADING];
  });
  render();

  // Create FormData with all files
  const formData = new FormData();
  pendingFiles.forEach(f => {
    formData.append('files', f.file);
  });
  // Include document type
  formData.append('document_type', state.documentType);

  try {
    // Use XMLHttpRequest for progress tracking
    const result = await uploadWithProgress(formData, pendingFiles);

    // Transition through extraction states with visual progress
    pendingFiles.forEach(f => {
      if (f.state !== UploadState.ERROR) {
        f.state = UploadState.EXTRACTING;
        f.progress = 0;
        f.statusMessage = StatusMessages[UploadState.EXTRACTING];
        f.currentStep = 2;
      }
    });
    render();

    // Simulate extraction progress (this happens server-side but we show visual progress)
    await simulateExtractionProgress(pendingFiles, 1500);

    // Transition to analyzing state
    pendingFiles.forEach(f => {
      if (f.state === UploadState.EXTRACTING) {
        f.state = UploadState.ANALYZING;
        f.progress = 0;
        f.statusMessage = StatusMessages[UploadState.ANALYZING];
        f.currentStep = 3;
      }
    });
    render();

    // Simulate analysis progress
    await simulateExtractionProgress(pendingFiles, 800);

    // Update file states based on response
    if (result.documents) {
      result.documents.forEach((doc, index) => {
        const file = pendingFiles[index];
        if (file) {
          file.state = UploadState.COMPLETE;
          file.result = doc;
          file.progress = 100;
          file.statusMessage = StatusMessages[UploadState.COMPLETE];
          file.currentStep = 4;
          // Add extracted info preview
          if (doc.extractedData) {
            file.extractedPreview = getExtractedPreview(doc.extractedData);
          }
        }
      });
    }

    // Handle errors with detailed messages
    if (result.errors) {
      result.errors.forEach((err) => {
        const file = state.files.find(f => f.file.name === err.filename);
        if (file) {
          file.state = UploadState.ERROR;
          file.error = err.error || 'Processing failed';
          file.statusMessage = `Error: ${err.error || 'Processing failed'}`;
        }
      });
    }

    // Call success callback with completed documents
    if (callbacks.onSuccess && result.documents && result.documents.length > 0) {
      callbacks.onSuccess(result.documents);
    }

  } catch (error) {
    console.error('Upload error:', error);
    pendingFiles.forEach(f => {
      f.state = UploadState.ERROR;
      f.error = error.message;
      f.statusMessage = StatusMessages[UploadState.ERROR];
    });

    // Call error callback
    if (callbacks.onError) {
      callbacks.onError(error);
    }
  } finally {
    state.isUploading = false;
    render();
  }
}

/**
 * Simulate progress through processing stages for better UX
 */
function simulateProcessingProgress(pendingFiles, startState, endState, duration, render) {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(95, Math.round((elapsed / duration) * 100));
      
      pendingFiles.forEach(f => {
        if (f.state === startState) {
          f.progress = progress;
          f.statusMessage = `${StatusMessages[startState]} ${progress}%`;
        }
      });
      render();
      
      if (elapsed >= duration) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
}

/**
 * Upload with XMLHttpRequest for progress tracking
 */
function uploadWithProgress(formData, pendingFiles) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let uploadStartTime = Date.now();

    // Track upload progress with detailed feedback
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percentComplete = Math.round((e.loaded / e.total) * 100);
        const elapsed = (Date.now() - uploadStartTime) / 1000;
        const speed = e.loaded / elapsed; // bytes per second
        const remaining = (e.total - e.loaded) / speed;
        
        // Format speed and remaining time
        const speedStr = speed > 1024 * 1024 
          ? `${(speed / (1024 * 1024)).toFixed(1)} MB/s`
          : `${(speed / 1024).toFixed(0)} KB/s`;
        const remainingStr = remaining < 60 
          ? `${Math.ceil(remaining)}s remaining`
          : `${Math.ceil(remaining / 60)}m remaining`;

        // Update progress for all files with detailed status
        pendingFiles.forEach(f => {
          f.progress = percentComplete;
          f.statusMessage = percentComplete < 100 
            ? `Uploading... ${percentComplete}% (${speedStr}, ${remainingStr})`
            : 'Upload complete, processing...';
          f.currentStep = 0; // Upload step
        });
        render();
      }
    });

    // Handle upload complete (before server response)
    xhr.upload.addEventListener('load', () => {
      const serverProcessingStart = Date.now();
      
      pendingFiles.forEach(f => {
        f.state = UploadState.UPLOAD_COMPLETE;
        f.progress = 100;
        f.statusMessage = StatusMessages[UploadState.UPLOAD_COMPLETE];
        f.currentStep = 1;
        f.serverProcessingStart = serverProcessingStart;
      });
      render();
      
      // Transition to storing state and show elapsed time
      setTimeout(() => {
        pendingFiles.forEach(f => {
          if (f.state === UploadState.UPLOAD_COMPLETE) {
            f.state = UploadState.STORING;
            f.statusMessage = StatusMessages[UploadState.STORING];
            f.currentStep = 1;
          }
        });
        render();
        
        // Update elapsed time every second while waiting for server
        const elapsedInterval = setInterval(() => {
          const stillProcessing = pendingFiles.some(f => 
            [UploadState.STORING, UploadState.UPLOAD_COMPLETE].includes(f.state)
          );
          
          if (!stillProcessing) {
            clearInterval(elapsedInterval);
            return;
          }
          
          pendingFiles.forEach(f => {
            if (f.state === UploadState.STORING && f.serverProcessingStart) {
              const elapsed = Math.round((Date.now() - f.serverProcessingStart) / 1000);
              f.statusMessage = `Processing on server... (${elapsed}s)`;
            }
          });
          render();
        }, 1000);
      }, 300);
    });

    // Handle response
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);
          resolve(result);
        } catch (e) {
          reject(new Error('Invalid server response'));
        }
      } else {
        let errorMessage = `Upload failed: ${xhr.statusText || xhr.status}`;
        try {
          const errorBody = JSON.parse(xhr.responseText);
          errorMessage = errorBody.detail || errorBody.message || errorMessage;
        } catch (e) {
          // Keep default error message
        }
        reject(new Error(errorMessage));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error - please check your connection and try again'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    // Open and send request with Authorization header
    xhr.open('POST', '/api/v1/documents/batch?auto_extract=true');
    xhr.setRequestHeader('Authorization', `Bearer ${getAuthToken()}`);
    xhr.send(formData);
  });
}

/**
 * Handle drag events
 */
function handleDragOver(e) {
  e.preventDefault();
  e.stopPropagation();
  state.isDragging = true;
  render();
}

function handleDragLeave(e) {
  e.preventDefault();
  e.stopPropagation();
  state.isDragging = false;
  render();
}

function handleDrop(e) {
  e.preventDefault();
  e.stopPropagation();
  state.isDragging = false;
  
  const files = e.dataTransfer?.files;
  if (files && files.length > 0) {
    addFiles(files);
  }
}

/**
 * Handle file input change
 */
function handleFileSelect(e) {
  const files = e.target.files;
  if (files && files.length > 0) {
    addFiles(files);
  }
  // Reset input so same file can be selected again
  e.target.value = '';
}

/**
 * Render a single file item with enhanced progress tracking
 */
function renderFileItem(fileInfo) {
  const { id, file, state: fileState, progress, error, result, statusMessage, currentStep, extractedPreview } = fileInfo;

  const stateClasses = {
    [UploadState.PENDING]: 'file-pending',
    [UploadState.UPLOADING]: 'file-uploading',
    [UploadState.UPLOAD_COMPLETE]: 'file-uploading',
    [UploadState.STORING]: 'file-storing',
    [UploadState.EXTRACTING]: 'file-extracting',
    [UploadState.ANALYZING]: 'file-analyzing',
    [UploadState.COMPLETE]: 'file-complete',
    [UploadState.ERROR]: 'file-error',
  };

  const stateIcons = {
    [UploadState.PENDING]: `<svg class="file-pending-icon" viewBox="0 0 24 24" width="20" height="20">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" opacity="0.5"/>
    </svg>`,
    [UploadState.UPLOADING]: '<div class="file-spinner"></div>',
    [UploadState.UPLOAD_COMPLETE]: '<div class="file-spinner"></div>',
    [UploadState.STORING]: '<div class="file-spinner file-spinner-storing"></div>',
    [UploadState.EXTRACTING]: '<div class="file-spinner file-spinner-extracting"></div>',
    [UploadState.ANALYZING]: '<div class="file-spinner file-spinner-analyzing"></div>',
    [UploadState.COMPLETE]: `<svg class="file-check" viewBox="0 0 24 24" width="20" height="20">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
      <polyline points="16 9 10.5 14.5 8 12" fill="none" stroke="currentColor" stroke-width="2"/>
    </svg>`,
    [UploadState.ERROR]: `<svg class="file-error-icon" viewBox="0 0 24 24" width="20" height="20">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
      <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" stroke-width="2"/>
      <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" stroke-width="2"/>
    </svg>`,
  };

  const isProcessing = [
    UploadState.UPLOADING, 
    UploadState.UPLOAD_COMPLETE, 
    UploadState.STORING, 
    UploadState.EXTRACTING, 
    UploadState.ANALYZING
  ].includes(fileState);
  
  const showProgress = isProcessing;
  const displayStatus = statusMessage || StatusMessages[fileState] || '';
  
  // Determine progress bar style based on state
  const getProgressBarClass = () => {
    if (fileState === UploadState.EXTRACTING) return 'extracting';
    if (fileState === UploadState.ANALYZING) return 'analyzing';
    if (fileState === UploadState.STORING) return 'storing';
    return '';
  };

  // Render progress steps indicator for processing files
  const renderProgressSteps = () => {
    if (!isProcessing && fileState !== UploadState.COMPLETE) return '';
    
    const steps = [
      { label: 'Upload', done: currentStep >= 1 || fileState === UploadState.COMPLETE },
      { label: 'Store', done: currentStep >= 2 || fileState === UploadState.COMPLETE },
      { label: 'Extract', done: currentStep >= 3 || fileState === UploadState.COMPLETE },
      { label: 'Analyze', done: currentStep >= 4 || fileState === UploadState.COMPLETE },
    ];
    
    return `
      <div class="progress-steps">
        ${steps.map((step, i) => `
          <div class="progress-step ${step.done ? 'done' : ''} ${currentStep === i ? 'active' : ''}">
            <div class="step-dot"></div>
            <span class="step-label">${step.label}</span>
          </div>
        `).join('')}
      </div>
    `;
  };

  // Render extracted preview for completed files
  const renderExtractedPreview = () => {
    if (fileState !== UploadState.COMPLETE || !extractedPreview) return '';
    return `
      <div class="extracted-preview">
        ${extractedPreview.map(item => `<span class="preview-item">${item}</span>`).join('')}
      </div>
    `;
  };

  return `
    <div class="upload-file-item ${stateClasses[fileState] || ''}" data-file-id="${id}">
      <div class="file-icon">
        ${getFileIcon(file.type)}
      </div>
      <div class="file-info">
        <div class="file-name">${file.name}</div>
        <div class="file-meta">
          <span class="file-size">${formatFileSize(file.size)}</span>
          <span class="file-status-text ${fileState === UploadState.ERROR ? 'error' : ''}">${displayStatus}</span>
        </div>
        ${error ? `<div class="file-error-msg">${error}</div>` : ''}
        ${showProgress ? `
          <div class="file-progress-container">
            <div class="file-progress-bar">
              <div class="file-progress-fill ${getProgressBarClass()}"
                   style="width: ${progress}%"></div>
            </div>
            <span class="file-progress-text">${progress}%</span>
          </div>
          ${renderProgressSteps()}
        ` : ''}
        ${renderExtractedPreview()}
      </div>
      <div class="file-status-icon">
        ${stateIcons[fileState] || ''}
      </div>
      ${!isProcessing && fileState !== UploadState.COMPLETE ? `
        <button class="file-remove-btn" data-action="remove" data-file-id="${id}">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2"/>
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2"/>
          </svg>
        </button>
      ` : ''}
    </div>
  `;
}

/**
 * Main render function
 */
function render() {
  const container = document.getElementById('batch-upload');
  if (!container) return;

  const pendingCount = state.files.filter(f => f.state === UploadState.PENDING).length;
  const processingCount = state.files.filter(f => 
    [UploadState.UPLOADING, UploadState.UPLOAD_COMPLETE, UploadState.STORING, 
     UploadState.EXTRACTING, UploadState.ANALYZING].includes(f.state)
  ).length;
  const completedCount = state.files.filter(f => f.state === UploadState.COMPLETE).length;
  const errorCount = state.files.filter(f => f.state === UploadState.ERROR).length;

  container.innerHTML = `
    <div class="batch-upload-container">
      <!-- Document Type Selector -->
      <div class="document-type-selector">
        <span class="type-label">${t('common.type')}:</span>
        <div class="type-toggle">
          <button type="button"
                  class="type-btn ${state.documentType === 'invoice' ? 'active' : ''}"
                  data-type="invoice"
                  id="type-invoice">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            ${t('upload.tabInvoice')}
          </button>
          <button type="button"
                  class="type-btn ${state.documentType === 'contract' ? 'active' : ''}"
                  data-type="contract"
                  id="type-contract">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <path d="M9 15l3 3 3-3"/>
            </svg>
            ${t('upload.tabContract')}
          </button>
        </div>
      </div>

      <!-- Drop Zone -->
      <div class="upload-dropzone ${state.isDragging ? 'dragging' : ''}" id="dropzone">
        <div class="dropzone-content">
          <svg class="dropzone-icon" viewBox="0 0 24 24" width="48" height="48">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" fill="none" stroke="currentColor" stroke-width="2"/>
            <polyline points="17 8 12 3 7 8" fill="none" stroke="currentColor" stroke-width="2"/>
            <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" stroke-width="2"/>
          </svg>
          <p class="dropzone-text">
            ${t('upload.dragDrop')} <label for="file-input" class="dropzone-browse">${t('upload.browse')}</label>
          </p>
          <p class="dropzone-hint">${t('upload.supportedFormats')}</p>
          <input type="file" id="file-input" class="file-input" multiple accept=".pdf,.png,.jpg,.jpeg,.csv,.xlsx,.xls" />
        </div>
      </div>

      ${state.files.length > 0 ? `
        <!-- File Queue -->
        <div class="upload-queue">
          <div class="queue-header">
            <h3 class="queue-title">Files (${state.files.length})</h3>
            <div class="queue-stats">
              ${pendingCount > 0 ? `<span class="queue-stat pending">${pendingCount} pending</span>` : ''}
              ${processingCount > 0 ? `<span class="queue-stat processing">${processingCount} processing</span>` : ''}
              ${completedCount > 0 ? `<span class="queue-stat complete">${completedCount} complete</span>` : ''}
              ${errorCount > 0 ? `<span class="queue-stat error">${errorCount} failed</span>` : ''}
            </div>
          </div>
          
          <div class="file-list">
            ${state.files.map(f => renderFileItem(f)).join('')}
          </div>

          <div class="queue-actions">
            <button class="btn btn-secondary" id="clear-files-btn" ${state.isUploading ? 'disabled' : ''}>
              Clear All
            </button>
            <button class="btn btn-primary" id="upload-files-btn" ${state.isUploading || pendingCount === 0 ? 'disabled' : ''}>
              ${state.isUploading ? t('upload.processing') : t('upload.processDocuments')}
            </button>
          </div>
        </div>
      ` : ''}
    </div>
  `;

  attachEventListeners();
}

/**
 * Attach event listeners
 */
function attachEventListeners() {
  const dropzone = document.getElementById('dropzone');
  const fileInput = document.getElementById('file-input');
  const uploadBtn = document.getElementById('upload-files-btn');
  const clearBtn = document.getElementById('clear-files-btn');
  const typeInvoiceBtn = document.getElementById('type-invoice');
  const typeContractBtn = document.getElementById('type-contract');

  if (dropzone) {
    dropzone.addEventListener('dragover', handleDragOver);
    dropzone.addEventListener('dragleave', handleDragLeave);
    dropzone.addEventListener('drop', handleDrop);
  }

  if (fileInput) {
    fileInput.addEventListener('change', handleFileSelect);
  }

  if (uploadBtn) {
    uploadBtn.addEventListener('click', uploadFiles);
  }

  if (clearBtn) {
    clearBtn.addEventListener('click', clearFiles);
  }

  // Document type buttons
  if (typeInvoiceBtn) {
    typeInvoiceBtn.addEventListener('click', () => setDocumentType(DocumentTypes.INVOICE));
  }
  if (typeContractBtn) {
    typeContractBtn.addEventListener('click', () => setDocumentType(DocumentTypes.CONTRACT));
  }

  // Remove buttons
  document.querySelectorAll('[data-action="remove"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const fileId = e.currentTarget.dataset.fileId;
      removeFile(fileId);
    });
  });
}

/**
 * Get document type from URL query parameter
 * @returns {string} - 'invoice' or 'contract'
 */
function getDocumentTypeFromUrl() {
  // Check window.currentQuery (set by main.js navigateTo)
  if (window.currentQuery?.type) {
    if (window.currentQuery.type === 'contract') return DocumentTypes.CONTRACT;
    if (window.currentQuery.type === 'invoice') return DocumentTypes.INVOICE;
  }

  // Fallback: check hash for query params (hash mode)
  const hash = window.location.hash;
  const queryIndex = hash.indexOf('?');
  if (queryIndex !== -1) {
    const hashParams = new URLSearchParams(hash.substring(queryIndex + 1));
    const hashType = hashParams.get('type');
    if (hashType === 'contract') return DocumentTypes.CONTRACT;
    if (hashType === 'invoice') return DocumentTypes.INVOICE;
  }

  // Fallback: check URL search params (history mode)
  const params = new URLSearchParams(window.location.search);
  const type = params.get('type');
  if (type === 'contract') return DocumentTypes.CONTRACT;
  if (type === 'invoice') return DocumentTypes.INVOICE;

  return DocumentTypes.INVOICE;
}

/**
 * Initialize the component
 */
function init(options = {}) {
  // Set callbacks from options
  callbacks.onSuccess = options.onSuccess || null;
  callbacks.onError = options.onError || null;

  // Set document type from options or URL parameter
  state.documentType = options.documentType || getDocumentTypeFromUrl();

  render();
}

/**
 * Create the batch upload component
 * @param {Object} options - Component options
 * @param {Function} options.onSuccess - Callback when upload succeeds
 * @param {Function} options.onError - Callback when upload fails
 * @param {string} options.documentType - Initial document type ('invoice' or 'contract')
 * @returns {HTMLElement}
 */
export function createBatchUpload(options = {}) {
  // Set callbacks
  callbacks.onSuccess = options.onSuccess || null;
  callbacks.onError = options.onError || null;

  // Reset state for fresh component
  state.files = [];
  state.isDragging = false;
  state.isUploading = false;

  // Set document type from options or URL parameter
  state.documentType = options.documentType || getDocumentTypeFromUrl();

  const container = document.createElement('div');
  container.id = 'batch-upload';
  container.className = 'batch-upload';

  // Initialize after adding to DOM
  setTimeout(() => {
    render();
  }, 0);

  return container;
}

export default {
  init,
  createBatchUpload,
  addFiles,
  clearFiles,
  uploadFiles,
  getState: () => state,
};
